import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  OrganizationSummaryDto,
  BatchOperationResultDto,
} from "../../api/types/dtos/organization.dto";
import { organizationService } from "../../api/services/organization.service";
import UploadReport from "./UploadReport";

interface ManualDeleteFarmFormProps {
  parentOrganizationId: string;
}

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="subtitle2" color="text.secondary">
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const ManualDeleteFarmForm: React.FC<ManualDeleteFarmFormProps> = ({ parentOrganizationId }) => {
  const [farmCode, setFarmCode] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<"form" | "result">("form");
  const [foundFarm, setFoundFarm] = useState<OrganizationSummaryDto | null>(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [result, setResult] = useState<BatchOperationResultDto | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const trimmedCode = farmCode.trim();

  useEffect(() => {
    if (!trimmedCode || !parentOrganizationId) {
      setFoundFarm(null);
      setLookupError(null);
      return;
    }
    setFoundFarm(null);
    setLookupError(null);
    setConfirmChecked(false);
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const check = await organizationService.checkExistingOrganizations(parentOrganizationId, {
          idNumbers: [trimmedCode],
        });
        const existing = check.existingOrgs?.[trimmedCode];
        if (existing) {
          setFoundFarm(existing);
        } else {
          setLookupError(`No se encontro una finca con el codigo "${trimmedCode}".`);
        }
      } catch {
        setLookupError("Error al verificar la finca. Intenta de nuevo.");
      } finally {
        setChecking(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [farmCode, parentOrganizationId, trimmedCode]);

  const isAlreadyInactive = !!foundFarm && foundFarm.isActive === false;

  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {};
    if (!trimmedCode) e.farmCode = "El codigo de finca es obligatorio";
    if (!foundFarm) e.farmCode = "No se encontro una finca con ese codigo";
    if (isAlreadyInactive) e.farmCode = "La finca ya esta inactiva";
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [trimmedCode, foundFarm, isAlreadyInactive]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    if (!foundFarm) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await organizationService.batchDeleteOrganizations(parentOrganizationId, {
        idNumbers: [trimmedCode],
      });
      setResult(res);
      setPhase("result");
    } catch (err: any) {
      setSubmitError(err?.message || "Ocurrio un error al eliminar la finca.");
    } finally {
      setSubmitting(false);
    }
  }, [validate, foundFarm, parentOrganizationId, trimmedCode]);

  const handleReset = useCallback(() => {
    setFarmCode("");
    setErrors({});
    setFoundFarm(null);
    setConfirmChecked(false);
    setSubmitError(null);
    setLookupError(null);
    setResult(null);
    setPhase("form");
  }, []);

  const isBusy = checking || submitting;
  const canDelete = !!foundFarm && !isAlreadyInactive && confirmChecked && !isBusy;

  if (phase === "result" && result) {
    return (
      <Box>
        <UploadReport
          reportData={result.created}
          updatedData={result.updated}
          skippedData={result.skipped}
          failedData={result.failed}
          mode="delete"
        />
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          <Button variant="contained" startIcon={<DeleteIcon />} onClick={handleReset}>
            Eliminar otra finca
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
        Eliminar finca manualmente
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ingresa el codigo de la finca a eliminar. Se buscara la finca para confirmar antes de
        desactivarla (eliminacion logica).
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <SectionHeader
            title="Codigo de Finca"
            subtitle="Solo se requiere el codigo (idNumber) de la finca."
          />
          <TextField
            label="Codigo de Finca *"
            fullWidth
            value={farmCode}
            onChange={(e) => {
              setFarmCode(e.target.value);
              setErrors({});
            }}
            error={!!errors.farmCode}
            helperText={errors.farmCode}
            disabled={isBusy}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        {checking && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Verificando finca...
            </Typography>
          </Box>
        )}

        {foundFarm && !isAlreadyInactive && (
          <Alert severity="info" icon={<CheckCircleIcon />}>
            <AlertTitle>Finca encontrada</AlertTitle>
            Se eliminara (desactivara) la finca <strong>{foundFarm.name}</strong> con codigo{" "}
            <strong>{foundFarm.idNumber}</strong>.
          </Alert>
        )}

        {isAlreadyInactive && (
          <Alert severity="warning">
            <AlertTitle>La finca ya esta inactiva</AlertTitle>
            La finca <strong>{foundFarm?.name}</strong> (codigo {foundFarm?.idNumber}) ya fue
            eliminada anteriormente. No es necesario eliminarla de nuevo.
          </Alert>
        )}

        {lookupError && !foundFarm && (
          <Alert severity="error">{lookupError}</Alert>
        )}

        {foundFarm && !isAlreadyInactive && (
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                disabled={isBusy}
                color="error"
              />
            }
            label="Entiendo que esta accion desactivara la finca (eliminacion logica)."
          />
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
          <Button variant="outlined" onClick={handleReset} disabled={isBusy}>
            Limpiar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmit}
            disabled={!canDelete}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {submitting ? "Eliminando..." : "Eliminar Finca"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ManualDeleteFarmForm;
